import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { 
  Menu, 
  X, 
  ChevronDown, 
  MessageCircle,
  Mail,
  Phone,
  MapPin,
  Instagram,
  Facebook
} from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { contactInfo, siteConfig } from '../lib/data';

const navLinks = [
  { name: 'Home', path: '/' },
  {
    name: 'Services',
    dropdown: [
      { name: 'Injury & Sports Treatments', path: '/services/injury-sports' },
      { name: 'Nutritional Therapy', path: '/services/nutritional-therapy' },
      { name: 'Once Off Services', path: '/services/once-off' },
      { name: 'Talks & Workshops', path: '/services/talks' },
    ],
  },
  {
    name: 'Resources',
    dropdown: [
      { name: 'Blog', path: '/resources/blog' },
      { name: 'Video Library', path: '/resources/videos' },
    ],
  },
  { name: 'Contact', path: '/contact' },
  { name: 'About Me', path: '/about' },
  { name: 'Testimonials', path: '/testimonials' },
];

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isMobileMenuOpen]);

  return (
    <>
      <header
        data-testid="navbar"
        className={`fixed top-0 left-0 right-0 z-[999] transition-all duration-300 ${
          isScrolled ? 'glassmorphism shadow-soft' : 'bg-transparent'
        }`}
      >
        <nav className="container-custom">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-3 shrink-0" data-testid="logo-link">
              <img
                src="/logo.avif"
                alt="Ali Dorgan"
                className="h-12 sm:h-14 w-auto"
              />
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden lg:flex items-center gap-1">
              {navLinks.map((link) =>
                link.dropdown ? (
                  <DropdownMenu key={link.name} modal={false}>
                    <DropdownMenuTrigger asChild>
                      <button 
                        className="flex items-center gap-1 px-4 py-2 text-sm font-medium text-charcoal hover:text-sage-500 transition-colors"
                        data-testid={`nav-${link.name.toLowerCase().replace(' ', '-')}`}
                      >
                        {link.name}
                        <ChevronDown className="w-4 h-4" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start" className="w-56 bg-white z-[1001]">
                      {link.dropdown.map((item) => (
                        <DropdownMenuItem key={item.path} asChild>
                          <Link
                            to={item.path}
                            className="w-full cursor-pointer"
                            data-testid={`nav-dropdown-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                          >
                            {item.name}
                          </Link>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                ) : (
                  <Link
                    key={link.path}
                    to={link.path}
                    className={`px-4 py-2 text-sm font-medium transition-colors ${
                      location.pathname === link.path
                        ? 'text-sage-500'
                        : 'text-charcoal hover:text-sage-500'
                    }`}
                    data-testid={`nav-${link.name.toLowerCase().replace(' ', '-')}`}
                  >
                    {link.name}
                  </Link>
                )
              )}
            </div>

            {/* CTA Button */}
            <div className="hidden lg:flex items-center gap-4">
              <Button
                asChild
                className="bg-[#25D366] hover:bg-[#20BD5C] text-white rounded-full px-6 gap-2"
                data-testid="header-message-btn"
              >
                <a href={contactInfo.whatsapp} target="_blank" rel="noopener noreferrer">
                  <MessageCircle className="w-4 h-4" />
                  Message Ali
                </a>
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="lg:hidden relative z-[1000] p-3 -mr-1 text-charcoal"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              data-testid="mobile-menu-btn"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </nav>
      </header>

      {/* Mobile Menu - Rendered OUTSIDE header to avoid backdrop-filter containing block issue */}
      {isMobileMenuOpen && (
        <div
          className="lg:hidden fixed inset-x-0 top-20 bottom-0 bg-cream-100 overflow-y-auto overscroll-contain"
          style={{ zIndex: 9999 }}
          data-testid="mobile-menu"
        >
          <div className="py-6 pb-24 space-y-2 px-6">
            {navLinks.map((link) =>
              link.dropdown ? (
                <div key={link.name} className="space-y-1">
                  <div className="px-4 py-2 text-sm font-medium text-muted-foreground">
                    {link.name}
                  </div>
                  {link.dropdown.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className="block px-8 py-2 text-sm text-charcoal hover:text-sage-500 hover:bg-sage-50 rounded-lg transition-colors"
                      data-testid={`mobile-nav-${item.name.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`block px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                    location.pathname === link.path
                      ? 'text-sage-500 bg-sage-50'
                      : 'text-charcoal hover:text-sage-500 hover:bg-sage-50'
                  }`}
                  data-testid={`mobile-nav-${link.name.toLowerCase().replace(' ', '-')}`}
                >
                  {link.name}
                </Link>
              )
            )}
            <div className="px-4 pt-4">
              <Button
                asChild
                className="w-full bg-[#25D366] hover:bg-[#20BD5C] text-white rounded-full gap-2"
                data-testid="mobile-message-btn"
              >
                <a href={contactInfo.whatsapp} target="_blank" rel="noopener noreferrer">
                  <MessageCircle className="w-4 h-4" />
                  Message Ali
                </a>
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

const Footer = () => {
  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Me', path: '/about' },
    { name: 'Services', path: '/services/injury-sports' },
    { name: 'Blog', path: '/resources/blog' },
    { name: 'Contact', path: '/contact' },
    { name: 'Book', path: '/book' },
  ];

  return (
    <footer className="bg-sage-900 text-cream-100" data-testid="footer">
      <div className="container-custom py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div className="lg:col-span-2">
            <img
              src="/logo.avif"
              alt="Ali Dorgan"
              className="h-14 w-auto mb-4 brightness-0 invert opacity-90"
            />
            <p className="text-sage-300 text-sm leading-relaxed max-w-md">
              Supporting your recovery, performance, and long-term health through hands-on 
              therapy and personalised nutrition.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-sans text-sm font-semibold uppercase tracking-wider mb-6">
              Quick Links
            </h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link
                    to={link.path}
                    className="text-sage-300 hover:text-white transition-colors text-sm"
                    data-testid={`footer-link-${link.name.toLowerCase().replace(' ', '-')}`}
                  >
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-sans text-sm font-semibold uppercase tracking-wider mb-6">
              Contact
            </h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-1 text-sage-400" />
                <span className="text-sage-300 text-sm">{contactInfo.location}</span>
              </li>
              <li className="flex items-start gap-3">
                <Mail className="w-4 h-4 mt-1 text-sage-400" />
                <a 
                  href={`mailto:${contactInfo.email}`} 
                  className="text-sage-300 hover:text-white transition-colors text-sm"
                  data-testid="footer-email"
                >
                  {contactInfo.email}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Phone className="w-4 h-4 mt-1 text-sage-400" />
                <a 
                  href={`tel:${contactInfo.phone.replace(/\s/g, '')}`} 
                  className="text-sage-300 hover:text-white transition-colors text-sm"
                  data-testid="footer-phone"
                >
                  {contactInfo.phone}
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Social & Trust Icons */}
        <div className="mt-10 flex items-center gap-5">
          <a
            href="https://www.doctify.com/ie/specialist/ali-dorgan"
            target="_blank"
            rel="noopener noreferrer"
            className="opacity-70 hover:opacity-100 transition-opacity duration-300"
            aria-label="View Ali on Doctify"
            data-testid="footer-doctify"
          >
            <img src="/doctify-logo.png" alt="Doctify" className="h-7 w-7" />
          </a>
          <a
            href="https://ntoi.ie"
            target="_blank"
            rel="noopener noreferrer"
            className="opacity-70 hover:opacity-100 transition-opacity duration-300"
            aria-label="NTOI Member"
            data-testid="footer-ntoi"
          >
            <img src="/ntoi-logo.png" alt="NTOI" className="h-7 w-auto" />
          </a>
          <a
            href="https://www.instagram.com/ali_dorgannutrition_neuromuscu?igsh=dmVzeDMxNTBkZTlh"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sage-400 hover:text-white transition-colors duration-300"
            aria-label="Follow Ali on Instagram"
            data-testid="footer-instagram"
          >
            <Instagram className="w-5 h-5" />
          </a>
          <a
            href="https://www.facebook.com/alidorgannutrition/"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sage-400 hover:text-white transition-colors duration-300"
            aria-label="Follow Ali on Facebook"
            data-testid="footer-facebook"
          >
            <Facebook className="w-5 h-5" />
          </a>
        </div>

        {/* Google Maps */}
        <div className="mt-10 rounded-xl overflow-hidden" data-testid="footer-map">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2471.5!2d-8.297!3d51.851!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4844a1c1e7b7a8e1%3A0x506dcd4a9a0c3f0!2sP24+PX86%2C+Cobh%2C+Co.+Cork!5e0!3m2!1sen!2sie!4v1"
            width="100%"
            height="180"
            style={{ border: 0, filter: 'grayscale(0.3)' }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="Ali Dorgan Clinic — P24 PX86, Cobh"
          />
        </div>
        <div className="mt-4 flex">
          <a
            href="https://www.google.com/maps/search/P24+PX86,+Cobh,+Cork,+Ireland"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 bg-sage-600 hover:bg-sage-500 text-white text-sm font-medium px-5 py-2.5 rounded-full transition-all duration-300 shadow-sm hover:shadow-md"
            data-testid="footer-directions-btn"
          >
            <MapPin className="w-4 h-4" />
            Get Directions
          </a>
        </div>

        {/* Disclaimer & Copyright */}
        <div className="mt-16 pt-8 border-t border-sage-800">
          <p className="text-sage-400 text-xs leading-relaxed mb-4 max-w-3xl">
            Information on this website is for educational purposes only and is not intended 
            as medical advice. Always consult a qualified healthcare provider.
          </p>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <p className="text-sage-500 text-xs">
              &copy; {new Date().getFullYear()} {siteConfig.name}. All rights reserved.
            </p>
            <p className="text-sage-600 text-xs" data-testid="footer-credit">
              <span>Website by </span>
              <span className="text-sage-500">Conor Walsh</span>
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};

const WhatsAppFloat = () => {
  return (
    <a
      href="https://wa.me/353877727580?text=Hi%20Ali%2C%20I%27d%20like%20to%20enquire%20about%20your%20services."
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Chat with Ali on WhatsApp"
      data-testid="whatsapp-float-btn"
      className="fixed bottom-20 sm:bottom-8 right-4 sm:right-6 z-40 flex items-center gap-2.5 bg-[#25D366] hover:bg-[#20BD5C] active:scale-95 text-white pl-4 pr-5 py-3 rounded-full shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5 group"
    >
      <MessageCircle className="w-5 h-5 transition-transform duration-300 group-hover:scale-110" />
      <span className="text-sm font-semibold whitespace-nowrap">Chat with Ali</span>
    </a>
  );
};

export const Layout = ({ children }) => {
  return (
    <div className="min-h-screen flex flex-col overflow-x-hidden">
      <Navbar />
      <main className="flex-1 pt-20 overflow-x-hidden">{children}</main>
      <Footer />
      <WhatsAppFloat />
    </div>
  );
};

export default Layout;
